class SensenCollection extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.eventController = new AbortController();
    this.requestController = null;
    this.form = this.querySelector('[data-sensen-filter-form]');
    this.filtersDropdown = this.querySelector('[data-sensen-unified-filters]');
    this.sortDropdown = this.querySelector('[data-sensen-sort-dropdown]');
    this.sortTrigger = this.sortDropdown?.querySelector('[data-sensen-sort-trigger]');
    this.statusElement = this.querySelector('[data-sensen-collection-status]');
    this.bindEvents();
  }

  disconnectedCallback() {
    if (this.eventController) this.eventController.abort();
    if (this.requestController) this.requestController.abort();
  }

  bindEvents() {
    const signal = this.eventController.signal;

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.renderUrl(this.buildFormUrl(), {
          updateHistory: true,
          keepFiltersOpen: true,
          focusControl: event.submitter || this.form.querySelector('[data-sensen-price-input]')
        });
      }, { signal });

      this.form.addEventListener('change', (event) => {
        if (!event.target.matches('[data-sensen-filter-input]')) return;

        this.renderUrl(this.buildFormUrl(), {
          updateHistory: true,
          keepFiltersOpen: true,
          focusControl: event.target
        });
      }, { signal });
    }

    if (this.filtersDropdown) {
      this.filtersDropdown.addEventListener('toggle', () => {
        if (this.filtersDropdown.open) {
          this.closeSort();
          this.updateDropdownMaxHeight(this.filtersDropdown.querySelector('.sensen-filter-panel-popover'));
        }
      }, { signal });
    }

    if (this.sortDropdown && this.sortTrigger && this.form) {
      this.sortTrigger.addEventListener('click', () => {
        const shouldOpen = !this.sortDropdown.classList.contains('is-open');
        this.closeFilters();
        this.sortDropdown.classList.toggle('is-open', shouldOpen);
        this.sortTrigger.setAttribute('aria-expanded', String(shouldOpen));
        if (shouldOpen) {
          this.updateDropdownMaxHeight(this.sortDropdown.querySelector('.sensen-custom-select-menu'));
        }
      }, { signal });

      this.sortDropdown.addEventListener('click', (event) => {
        const sortOption = event.target.closest('[data-sensen-sort-option]');
        if (!sortOption) return;

        const sortInput = this.form.querySelector('[data-sensen-sort-value]');
        if (sortInput) sortInput.value = sortOption.dataset.value || '';
        this.closeSort();
        this.renderUrl(this.buildFormUrl(), { updateHistory: true });
      }, { signal });
    }

    this.addEventListener('click', (event) => {
      const navigationLink = event.target.closest('[data-sensen-filter-link], .sensen-pagination a');
      if (!navigationLink) return;

      const targetUrl = new URL(navigationLink.href, window.location.origin);
      if (targetUrl.origin !== window.location.origin) return;

      event.preventDefault();
      this.closeFilters();
      this.renderUrl(targetUrl, {
        updateHistory: true,
        scrollToResults: navigationLink.closest('.sensen-pagination') !== null
      });
    }, { signal });

    document.addEventListener('click', (event) => {
      if (this.filtersDropdown && !this.filtersDropdown.contains(event.target)) {
        this.closeFilters();
      }
      if (this.sortDropdown && !this.sortDropdown.contains(event.target)) {
        this.closeSort();
      }
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.filtersDropdown && this.filtersDropdown.open) {
        this.closeFilters();
        const summary = this.filtersDropdown.querySelector('summary');
        if (summary) summary.focus();
      }
      if (event.key === 'Escape' && this.sortDropdown?.classList.contains('is-open')) {
        this.closeSort();
        this.sortTrigger?.focus();
      }
    }, { signal });

    window.addEventListener('popstate', () => {
      this.renderUrl(new URL(window.location.href));
    }, { signal });
  }

  buildFormUrl() {
    const targetUrl = new URL(this.form.action, window.location.origin);
    const formData = new FormData(this.form);

    formData.forEach((value, key) => {
      const normalizedValue = String(value).trim();
      if (normalizedValue !== '') targetUrl.searchParams.append(key, normalizedValue);
    });

    targetUrl.searchParams.delete('page');
    return targetUrl;
  }

  updateDropdownMaxHeight(menuElement) {
    if (!menuElement) return;
    const rect = menuElement.getBoundingClientRect();
    const availableHeight = window.innerHeight - rect.top - 16;
    if (availableHeight > 100) {
      menuElement.style.maxHeight = `${Math.floor(availableHeight)}px`;
    }
  }

  closeFilters() {
    if (this.filtersDropdown) this.filtersDropdown.removeAttribute('open');
  }

  closeSort() {
    if (this.sortDropdown) {
      this.sortDropdown.classList.remove('is-open');
      const menu = this.sortDropdown.querySelector('.sensen-custom-select-menu');
      if (menu) menu.style.maxHeight = '';
    }
    if (this.sortTrigger) this.sortTrigger.setAttribute('aria-expanded', 'false');
  }

  getControlState(control) {
    if (!control) return null;

    if (control.matches('[data-sensen-filter-apply]')) {
      return { type: 'apply' };
    }

    if (!control.name) return null;

    return {
      name: control.name,
      value: control.value,
      type: control.type
    };
  }

  restoreControlFocus(collection, controlState) {
    if (!controlState) return;

    if (controlState.type === 'apply') {
      collection.querySelector('[data-sensen-filter-apply]')?.focus({ preventScroll: true });
      return;
    }

    const controls = collection.querySelectorAll('[data-sensen-filter-input], [data-sensen-price-input]');
    const targetControl = Array.from(controls).find((control) => {
      if (control.name !== controlState.name || control.type !== controlState.type) return false;
      if (control.type === 'checkbox' || control.type === 'radio') return control.value === controlState.value;
      return true;
    });

    if (targetControl) targetControl.focus({ preventScroll: true });
  }

  async renderUrl(targetUrl, options = {}) {
    const {
      updateHistory = false,
      scrollToResults = false,
      keepFiltersOpen = false,
      focusControl = null
    } = options;
    const shouldReopenFilters = keepFiltersOpen && Boolean(this.filtersDropdown && this.filtersDropdown.open);
    const controlState = this.getControlState(focusControl);

    if (this.requestController) this.requestController.abort();
    this.requestController = new AbortController();

    this.setAttribute('aria-busy', 'true');
    if (this.statusElement) this.statusElement.textContent = this.dataset.loadingMessage || '';

    const sectionUrl = new URL(targetUrl.toString());
    sectionUrl.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(sectionUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.requestController.signal
      });

      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextCollection = documentFragment.querySelector('sensen-collection');

      if (!nextCollection) throw new Error('Collection section was not returned');

      if (updateHistory) {
        window.history.pushState({}, '', targetUrl.toString());
      }

      const updatedMessage = nextCollection.dataset.updatedMessage || '';

      this.replaceWith(nextCollection);

      if (shouldReopenFilters) {
        const nextFiltersDropdown = nextCollection.querySelector('[data-sensen-unified-filters]');
        if (nextFiltersDropdown) {
          nextFiltersDropdown.setAttribute('open', '');
          nextCollection.updateDropdownMaxHeight(nextFiltersDropdown.querySelector('.sensen-filter-panel-popover'));
        }
      }

      const nextStatus = nextCollection.querySelector('[data-sensen-collection-status]');
      if (nextStatus) nextStatus.textContent = updatedMessage;

      nextCollection.restoreControlFocus(nextCollection, controlState);

      if (scrollToResults) {
        nextCollection.scrollIntoView({ block: 'start' });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(targetUrl.toString());
    } finally {
      if (this.isConnected) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('sensen-collection')) {
  customElements.define('sensen-collection', SensenCollection);
}
